import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Container,
    Grid,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    Legend,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
} from 'recharts';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import api from '../../../services/api';

interface SystemMetrics {
    totalScans: number;
    uniqueVisitors: number;
    formCompletions: number;
    conversionRate: number;
    redemptionRate: number;
}

interface TimeSeriesDataPoint {
    date: string;
    displayDate: string;
    scans: number;
    formCompletions: number;
    redemptions: number;
}

interface DeviceData {
    total: number;
    conversions: number;
    conversionRate: number;
}

interface DeviceAnalytics {
    mobile: DeviceData;
    tablet: DeviceData;
    desktop: DeviceData;
}

interface BrowserAnalytics {
    name: string;
    version: string;
    total: number;
    conversions: number;
    conversionRate: number;
}

interface TopCoupon {
    id: string;
    title: string;
    scans: number;
    conversions: number;
    redemptions: number;
    conversionRate: number;
}

interface UsagePattern {
    hour: number;
    scans: number;
    conversions: number;
    conversionRate: number;
}

interface PerformanceMetrics {
    avgTimeToRedeem: number;
    peakHour: number;
    topConversionRate: number;
}

interface SystemAnalytics {
    metrics: SystemMetrics;
    timeSeriesData: TimeSeriesDataPoint[];
    deviceAnalytics: DeviceAnalytics;
    browserAnalytics: BrowserAnalytics[];
    topCoupons: TopCoupon[];
    usagePatterns: UsagePattern[];
    performanceMetrics: PerformanceMetrics;
}

const DEVICE_COLORS = {
    mobile: '#8884d8',
    tablet: '#82ca9d',
    desktop: '#ffc658'
};

const formatDate = (dateStr: string): string => {
    const date = new Date(`${dateStr}T00:00:00-05:00`);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const processTimeSeriesData = (data: any[]): TimeSeriesDataPoint[] => {
    return data.map(item => ({
        ...item,
        displayDate: formatDate(item.date)
    }));
};

const Reports: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [timeRange, setTimeRange] = useState('7d');
    const [data, setData] = useState<SystemAnalytics | null>(null);

    useEffect(() => {
        const fetchAnalytics = async () => {
            try {
                setLoading(true);
                const response = await api.get('/admin/analytics/system', {
                    params: { timeRange }
                });
                const rawData = response.data.data;
                setData({
                    ...rawData,
                    timeSeriesData: processTimeSeriesData(rawData.timeSeriesData)
                });
                setError(null);
            } catch (err: any) {
                console.error('Failed to fetch system analytics:', err);
                setError(err.response?.data?.message || 'Failed to load system analytics');
            } finally {
                setLoading(false);
            }
        };

        fetchAnalytics();
    }, [timeRange]);

    const prepareDeviceData = () => {
        if (!data?.deviceAnalytics) return [];
        return Object.entries(data.deviceAnalytics).map(([type, stats]) => ({
            name: type.charAt(0).toUpperCase() + type.slice(1),
            value: stats.total,
            conversions: stats.conversions,
            conversionRate: stats.conversionRate
        }));
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    // Prepare device data for pie chart
    const deviceData = [
        { name: 'Mobile', value: data?.deviceAnalytics.mobile.total || 0 },
        { name: 'Tablet', value: data?.deviceAnalytics.tablet.total || 0 },
        { name: 'Desktop', value: data?.deviceAnalytics.desktop.total || 0 }
    ].filter(item => item.value > 0); // Only show devices with data

    // Prepare browser data
    const browserData = data?.browserAnalytics.map(browser => ({
        name: `${browser.name}${browser.version ? ` ${browser.version}` : ''}`,
        scans: browser.total,
        conversions: browser.conversions
    })) || [];

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            {/* Header Controls */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'flex-end' }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Time Range</InputLabel>
                    <Select
                        value={timeRange}
                        label="Time Range"
                        onChange={(e) => setTimeRange(e.target.value)}
                    >
                        <MenuItem value="7d">Last 7 Days</MenuItem>
                        <MenuItem value="30d">Last 30 Days</MenuItem>
                        <MenuItem value="90d">Last 90 Days</MenuItem>
                        <MenuItem value="all">All Time</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* Metrics Cards */}
            <Grid container spacing={3} mb={3}>
                <Grid item xs={12} sm={6} md={2.4}>
                    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box display="flex" alignItems="center" mb={1}>
                            <QrCodeScannerIcon color="primary" />
                            <Typography variant="subtitle2" ml={1}>Total Scans</Typography>
                        </Box>
                        <Typography variant="h4">{data?.metrics.totalScans || 0}</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box display="flex" alignItems="center" mb={1}>
                            <PersonIcon color="primary" />
                            <Typography variant="subtitle2" ml={1}>Unique Visitors</Typography>
                        </Box>
                        <Typography variant="h4">{data?.metrics.uniqueVisitors || 0}</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box display="flex" alignItems="center" mb={1}>
                            <AssignmentTurnedInIcon color="primary" />
                            <Typography variant="subtitle2" ml={1}>Form Completions</Typography>
                        </Box>
                        <Typography variant="h4">{data?.metrics.formCompletions || 0}</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box display="flex" alignItems="center" mb={1}>
                            <TrendingUpIcon color="primary" />
                            <Typography variant="subtitle2" ml={1}>Conversion Rate</Typography>
                        </Box>
                        <Typography variant="h4">{data?.metrics.conversionRate || 0}%</Typography>
                        <Typography variant="caption" color="textSecondary">From Total Scans</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box display="flex" alignItems="center" mb={1}>
                            <ShoppingCartIcon color="primary" />
                            <Typography variant="subtitle2" ml={1}>Redemption Rate</Typography>
                        </Box>
                        <Typography variant="h4">{data?.metrics.redemptionRate || 0}%</Typography>
                        <Typography variant="caption" color="textSecondary">From Form Completion</Typography>
                    </Paper>
                </Grid>
            </Grid>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Scans & Redemptions Over Time</Typography>
                <Box height={400}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                            data={data?.timeSeriesData || []}
                            margin={{
                                top: 5,
                                right: 30,
                                left: 20,
                                bottom: 5,
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="displayDate"
                                tick={{ fontSize: 12 }}
                            />
                            <YAxis tick={{ fontSize: 12 }} />
                            <RechartsTooltip />
                            <Legend />
                            <Line
                                type="monotone"
                                dataKey="scans"
                                stroke="#8884d8"
                                name="Scans"
                                strokeWidth={2}
                            />
                            <Line
                                type="monotone"
                                dataKey="formCompletions"
                                stroke="#82ca9d"
                                name="Form Completions"
                                strokeWidth={2}
                            />
                            <Line
                                type="monotone"
                                dataKey="redemptions"
                                stroke="#ffc658"
                                name="Redemptions"
                                strokeWidth={2}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            </Paper>

            <Grid container spacing={3} sx={{ mb: 6 }}>
                <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 3, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>Device Distribution</Typography>
                        <Box sx={{ width: '100%', height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie
                                        data={deviceData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={true}
                                        outerRadius={80}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {deviceData.map((entry, index) => (
                                            <Cell 
                                                key={`cell-${index}`} 
                                                fill={DEVICE_COLORS[entry.name.toLowerCase() as keyof typeof DEVICE_COLORS]} 
                                            />
                                        ))}
                                    </Pie>
                                    <Legend verticalAlign="bottom" height={36} />
                                    <RechartsTooltip
                                        content={({ active, payload }) => {
                                            if (active && payload && payload.length) {
                                                const data = payload[0].payload;
                                                const total = deviceData.reduce((sum, item) => sum + item.value, 0);
                                                const percentage = ((data.value / total) * 100).toFixed(1);
                                                return (
                                                    <Box sx={{ bgcolor: 'background.paper', p: 1, border: '1px solid #ccc' }}>
                                                        <Typography variant="body2">{`${data.name}`}</Typography>
                                                        <Typography variant="body2">{`Total: ${data.value}`}</Typography>
                                                        <Typography variant="body2">{`Percentage: ${percentage}%`}</Typography>
                                                    </Box>
                                                );
                                            }
                                            return null;
                                        }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 3, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>Top Browsers</Typography>
                        <Box sx={{ width: '100%', height: 300 }}>
                            <ResponsiveContainer>
                                <BarChart
                                    data={browserData}
                                    margin={{
                                        top: 5,
                                        right: 30,
                                        left: 20,
                                        bottom: 60
                                    }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="name"
                                        tick={{ fontSize: 12 }}
                                        interval={0}
                                        angle={-45}
                                        textAnchor="end"
                                        height={60}
                                    />
                                    <YAxis />
                                    <RechartsTooltip
                                        content={({ active, payload }) => {
                                            if (active && payload && payload.length) {
                                                const data = payload[0].payload;
                                                return (
                                                    <Box sx={{ bgcolor: 'background.paper', p: 1, border: '1px solid #ccc' }}>
                                                        <Typography variant="body2">{`${data.name}`}</Typography>
                                                        <Typography variant="body2">{`Scans: ${data.scans}`}</Typography>
                                                        <Typography variant="body2">{`Conversions: ${data.conversions}`}</Typography>
                                                    </Box>
                                                );
                                            }
                                            return null;
                                        }}
                                    />
                                    <Legend />
                                    <Bar dataKey="scans" name="Scans" fill="#8884d8" />
                                    <Bar dataKey="conversions" name="Conversions" fill="#82ca9d" />
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            <Grid container spacing={3} sx={{ mb: 6 }}>
                <Grid item xs={12} sm={4}>
                    <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            Average Time to Redeem
                        </Typography>
                        <Typography variant="h4">
                            {data?.performanceMetrics.avgTimeToRedeem || 0}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            minutes
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            Peak Activity Hour
                        </Typography>
                        <Typography variant="h4">
                            {data?.performanceMetrics.peakHour || 0}:00
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            24-hour format (EST)
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            Best Conversion Rate
                        </Typography>
                        <Typography variant="h4">
                            {data?.performanceMetrics.topConversionRate.toFixed(1) || 0}%
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            among all coupons
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            <Box sx={{ mb: 2 }}>
                <Typography variant="h6">Top Performing Coupons</Typography>
            </Box>
            <Grid container spacing={3} sx={{ mb: 3 }}>
                {data?.topCoupons.slice(0, 5).map((coupon) => (
                    <Grid item xs={12} sm={6} md={2.4} key={coupon.id}>
                        <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="subtitle2" color="textSecondary" noWrap gutterBottom>
                                {coupon.title}
                            </Typography>
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box>
                                    <Typography variant="body2" color="textSecondary">Scans</Typography>
                                    <Typography variant="h6">{coupon.scans}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="textSecondary">Conversions</Typography>
                                    <Typography variant="h6">{coupon.conversions}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" color="textSecondary">Rate</Typography>
                                    <Typography variant="h6">{coupon.conversionRate}%</Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Hourly Usage Patterns</Typography>
                <Box height={300}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={data?.usagePatterns.map(pattern => ({
                                ...pattern,
                                hour: `${pattern.hour}:00`,
                            }))}
                            margin={{
                                top: 5,
                                right: 30,
                                left: 20,
                                bottom: 25,
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="hour"
                                tick={{ fontSize: 12 }}
                                interval={0}
                                angle={-45}
                                textAnchor="end"
                                height={60}
                            />
                            <YAxis tick={{ fontSize: 12 }} />
                            <RechartsTooltip
                                content={({ active, payload }) => {
                                    if (active && payload && payload.length) {
                                        const data = payload[0].payload;
                                        return (
                                            <Box sx={{ bgcolor: 'background.paper', p: 1, border: '1px solid #ccc' }}>
                                                <Typography variant="body2">{`Time: ${data.hour}`}</Typography>
                                                <Typography variant="body2">{`Scans: ${data.scans}`}</Typography>
                                                <Typography variant="body2">{`Conversions: ${data.conversions}`}</Typography>
                                                <Typography variant="body2">{`Rate: ${data.conversionRate}%`}</Typography>
                                            </Box>
                                        );
                                    }
                                    return null;
                                }}
                            />
                            <Legend />
                            <Bar dataKey="scans" fill="#8884d8" name="Total Scans" />
                            <Bar dataKey="conversions" fill="#82ca9d" name="Conversions" />
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            </Paper>
        </Container>
    );
};

export default Reports;
